import { useCurrentLang, useProfileInfo, useTranslator, useUserInfo } from '$hooks';
import './AuthPopover.sass';
import { profileActions, profileSelector } from '$store/profile';
import { useSelector } from 'react-redux';
import { Auth } from '$shared/components';
import { incline } from 'lvovich';
import { classNames, transliterate } from '$utils';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IcClose16 } from '$assets';
import { store } from '$store/store';
import { authSelector } from '$store/auth';
import { AuthPopoverState } from '$store/models';

export const AuthPopoverSimple = () => {
  const t = useTranslator();
  const { authPopoverState } = useSelector(profileSelector);
  const { isLogged } = useSelector(authSelector);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleClose = useCallback(() => {
    store.dispatch(profileActions.setAuthPopoverState(null));
  }, []);

  if (isLogged || !authPopoverState) {
    return null;
  }

  return (
    <div
      className={classNames('AuthPopover__wrapper', {
        _hidden: !authPopoverState,
      })}
    >
      <div className="AuthPopover__bg" />
      <div className="AuthPopover">
        <div className="AuthPopover__body">
          <div className="AuthPopover__content" style={{ paddingTop: 24 }}>
            <div
              className="AuthPopoverSimple__header"
              style={{ justifyContent: isEmailSent ? 'flex-end' : 'space-between' }}
            >
              {!isEmailSent &&
                t(
                  authPopoverState === AuthPopoverState.LOGIN
                    ? 'auth_title_login'
                    : 'auth_title_create_account',
                )}
              <div className="AuthPopover__header__close" onClick={handleClose}>
                <IcClose16 />
              </div>
            </div>
            {/*<div className="AuthPopover__text">{t('auth_caption')}</div>*/}
            <Auth onEmailSendClick={(isEmailSent) => setIsEmailSent(isEmailSent)} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthPopoverCommon = () => {
  const t = useTranslator();
  const { authPopoverState } = useSelector(profileSelector);
  const { isLogged } = useSelector(authSelector);

  const handleClose = useCallback(() => {
    store.dispatch(profileActions.setAuthPopoverState(null));
  }, []);

  if (isLogged || !authPopoverState) {
    return null;
  }

  return (
    <div
      className={classNames('AuthPopover__wrapper', {
        _hidden: !authPopoverState,
      })}
    >
      <div className="AuthPopover__bg" />
      <div className="AuthPopover">
        <div className="AuthPopover__body">
          <div className="AuthPopover__header__close" onClick={handleClose}>
            <IcClose16 />
          </div>
          <div className="AuthPopover__avatars">
            <img
              className="AuthPopover__avatar aside left"
              src={require('./assets/model1.png').default}
              alt="Avatar image"
            />
            <img
              className="AuthPopover__avatar"
              src={require('./assets/model2.png').default}
              alt="Avatar image"
            />
            <img
              className="AuthPopover__avatar aside right"
              src={require('./assets/model3.png').default}
              alt="Avatar image"
            />
          </div>
          <div className="AuthPopover__content">
            <div className="AuthPopover__header">{t('auth_popover_header_common')}</div>
            <div className="AuthPopover__text">{t('auth_popover_text_common')}</div>
            <Auth />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthPopover = ({ userId }: { userId: number }) => {
  const t = useTranslator();
  const user = useUserInfo(userId);
  const { isLogged } = useSelector(authSelector);
  const { firstName, photo, verified, lastName } = user;
  const { coverUrl } = useProfileInfo(userId);
  const { authPopoverState } = useSelector(profileSelector);
  const currentLang = useCurrentLang();
  const isRU = currentLang.key === 'ru';
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isModelProfile = verified === 2;

  const { first: firstNameInclined } = useMemo(() => {
    return isRU
      ? incline(
          {
            first: firstName ?? '',
            last: lastName ?? '',
          },
          'genitive',
        )
      : {
          first: `${transliterate(firstName ?? '')}'s`,
        };
  }, [firstName, lastName, isRU]);

  const handleClose = useCallback(() => {
    store.dispatch(profileActions.setAuthPopoverState(null));
  }, []);

  if (isLogged || !authPopoverState) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      className={classNames('AuthPopover__wrapper', {
        _hidden: !authPopoverState,
      })}
    >
      <div className="AuthPopover__bg" />
      <div className="AuthPopover">
        <div className="AuthPopover__body">
          <div className="AuthPopover__header__close" onClick={handleClose}>
            <IcClose16 />
          </div>
          {coverUrl && (
            <img className="AuthPopover__cover" src={coverUrl} alt="Cover image" />
          )}
          <img
            className="AuthPopover__avatar"
            src={photo}
            alt="Avatar image"
            style={{ marginTop: coverUrl ? -45 : 24 }}
          />
          <div className="AuthPopover__content">
            <div className="AuthPopover__header">
              {t('auth_popover_header', { name: firstNameInclined })}
            </div>
            <div className="AuthPopover__text">
              {t(
                isModelProfile ? 'auth_popover_text_models' : 'auth_popover_text_default',
              )}
            </div>
            <Auth />
          </div>
        </div>
      </div>
    </div>
  );
};
